import {
  OpenAICompatibleChatLanguageModel,
  OpenAICompatibleCompletionLanguageModel,
  OpenAICompatibleEmbeddingModel,
  type ProviderErrorStructure,
} from '@ai-sdk/openai-compatible';
import {
  type EmbeddingModelV2,
  type ImageModelV2,
  type LanguageModelV2,
  type ProviderV2,
} from '@ai-sdk/provider';
import {
  type FetchFunction,
  loadApiKey,
  withoutTrailingSlash,
  type Resolvable,
} from '@ai-sdk/provider-utils';
import { aimlapiErrorSchema } from './aimlapi-error';
import {
  type AIMLAPIImageModelId,
  type AimlapiImageSettings,
} from './aimlapi-image-settings';
import { AimlapiImageModel } from './aimlapi-image-model';
import { z } from 'zod';

export interface AIMLAPIProviderSettings {
  apiKey?: string;
  baseURL?: string;
  headers?: Resolvable<Record<string, string | undefined>>;
  fetch?: FetchFunction;
}

export interface AIMLAPIProvider extends ProviderV2 {
  (modelId: string): LanguageModelV2;

  chat(modelId: string): LanguageModelV2;
  completion(modelId: string): LanguageModelV2;
  languageModel(modelId: string): LanguageModelV2;
  textEmbeddingModel(modelId: string): EmbeddingModelV2<string>;
  imageModel(
    modelId: string,
    settings?: AimlapiImageSettings,
  ): ImageModelV2;
}

const defaultBaseURL = 'https://api.aimlapi.com/v1';

// ❗ Используем тип, ВЫВЕДЕННЫЙ ИЗ СХЕМЫ:
type AimErr = z.infer<typeof aimlapiErrorSchema>;

const aimlapiErrorStructure: ProviderErrorStructure<AimErr> = {
  errorSchema: aimlapiErrorSchema,
  errorToMessage: (data) => data.error.message,
  // isRetryable: (res) => res.status === 429 || res.status >= 500,
};

export function createAIMLAPI(
  options: AIMLAPIProviderSettings = {},
): AIMLAPIProvider {
  const rawBaseURL = options.baseURL ?? defaultBaseURL;
  const strictBaseURL = withoutTrailingSlash(rawBaseURL) ?? defaultBaseURL;

  const getHeadersLoose = (): Record<string, string | undefined> => ({
    Authorization: `Bearer ${loadApiKey({
      apiKey: options.apiKey,
      environmentVariableName: 'AIMLAPI_API_KEY',
      description: 'AIMLAPI API key',
    })}`,
    'X-Title': 'vercel',
    ...(typeof options.headers === 'function'
      ? (options.headers as any)()
      : options.headers),
  });

  const getHeadersStrict = (): Record<string, string> =>
    Object.fromEntries(
      Object.entries(getHeadersLoose()).filter(
        ([, v]) => v !== undefined,
      ) as Array<[string, string]>,
    );

  interface CommonModelConfig {
    provider: string;
    url: ({ path }: { path: string }) => string;
    headers: () => Record<string, string>;
    fetch?: FetchFunction;
    errorStructure?: ProviderErrorStructure<AimErr>;
  }

  const getCommonModelConfig = (modelType: string): CommonModelConfig => ({
    provider: `aimlapi.${modelType}`,
    url: ({ path }) => `${strictBaseURL}${path}`,
    headers: getHeadersStrict,
    fetch: options.fetch,
    errorStructure: aimlapiErrorStructure,
  });

  const createChat = (modelId: string) =>
    new OpenAICompatibleChatLanguageModel(modelId, getCommonModelConfig('chat'));

  const createCompletion = (modelId: string) =>
    new OpenAICompatibleCompletionLanguageModel(
      modelId,
      getCommonModelConfig('completion'),
    );

  const createEmbedding = (modelId: string) =>
    new OpenAICompatibleEmbeddingModel(
      modelId,
      getCommonModelConfig('embedding'),
    );

  const createImage = (
    modelId: AIMLAPIImageModelId,
    settings: AimlapiImageSettings = {},
  ) =>
    new AimlapiImageModel(modelId, settings, {
      provider: 'aimlapi.image',
      baseURL: strictBaseURL,
      headers: getHeadersStrict,
      fetch: options.fetch,
    });

  const providerFunc = (modelId: string) => createChat(modelId);
  (providerFunc as any).chat = createChat;
  (providerFunc as any).completion = createCompletion;
  (providerFunc as any).languageModel = createChat;
  (providerFunc as any).textEmbeddingModel = createEmbedding;
  (providerFunc as any).imageModel = createImage;

  return providerFunc as unknown as AIMLAPIProvider;
}

export const aimlapi = createAIMLAPI();
